import React, { useEffect, useState } from 'react';
import { apiRequest } from '../../../service/apiRequest';
import {
  Table,
  Button,
  Flex,
  Breadcrumb,
  Form,
  message,
  Popconfirm,
  Space,
  Pagination,
  Switch,
  TableProps,
  InputNumber,
  Select,
  DatePicker,
  Modal,
  Input,
  Descriptions
} from 'antd';
import { createStyles } from 'antd-style';
import { HomeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import SubscriptionModal from './SubscriptionModal';
import { showSuccess, showError } from '../../../components/errorHandler';
import type { PopconfirmProps, TableColumnsType } from 'antd';
import { useCurrentModule } from '../../../hooks/useCurrentModule';
import PaymentDetailsModal from './PaymentDetailsModal';
import PaymentModal from './PaymentModal';

const useStyle = createStyles(({ css }) => ({
  customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));
interface Payment {
  sp_payment_id: number;
  sp_payment_amount: number;
  sp_payment_date: string;
  sp_payment_method: string;
  sp_payment_status: string;
}
interface Subscription {
  subscription_id: number;
  organization_name: string;
  subscription_organization_id: string;
  subscription_status: number;
  subscription_package_id: number;
  subscription_expire_status: string;
  subscription_expiry_date: string;
  package_name: string;
  subcription_amount: number;
  subcription_discount: number;
  subcription_after_discount_amount: number;
}

interface SubscriptionDetail {
  subscription_id: number;
  organization_name: string;
  subscription_organization_id: string;
  subscription_status: number;
  subscription_package_id: number;
  subscription_expire_status: string;
  subscription_expiry_date: string;
  package_name: string;
  subcription_amount: number;
  subcription_discount: number;
  subcription_after_discount_amount: number;
}

const SubscriptionMaster: React.FC = () => {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]); // default

  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<Subscription | null>(null);
  const { styles } = useStyle();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const currentModule = useCurrentModule();

  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<any>({});


  const [ispaymentnowModalVisible, setIsPaymentNowModalVisible] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState<SubscriptionDetail | null>(null);


  const [ispaymentviewModalVisible, setIsPaymentViewModalVisible] = useState(false);


  const [payments, setPayments] = useState<Payment[]>([]);


  const cancel: PopconfirmProps['onCancel'] = (e) => {
    console.log(e);
    showError('Click on No');
  };

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const fetchSubscription = async () => {
    try {
      const data = await apiRequest<Subscription[]>({
        method: 'get',
        url: 'subscriptions',
      });
      setSubscriptions(data);
    } catch (error) {
      showError('Failed to fetch subscriptions');
      console.error(error);
    }
  };

  useEffect(() => {
    fetchSubscription();
  }, []);

  const handleEditClick = (record: Subscription) => {
    setIsEditMode(true);
    setInitialValues({
      subscription_id: record.subscription_id,
      subscription_organization_id: record.subscription_organization_id,
      organization_name: record.organization_name,
      subscription_status: record.subscription_status,
      subscription_package_id: record.subscription_package_id,
      package_name: record.package_name,
      subscription_expiry_date: record.subscription_expiry_date,
      subscription_expire_status: record.subscription_expire_status,
      subcription_amount: record.subcription_amount,
      subcription_discount: record.subcription_discount,
      subcription_after_discount_amount: record.subcription_after_discount_amount
    });
    setIsModalVisible(true);
  };

  const openPaymentNowModal = (subscription: any) => {
    // You can save subscriptionId in state if you want to use it inside the modal
    setSelectedSubscription(subscription);
    setIsPaymentNowModalVisible(true);
  };

  const openPaymentViewModal = (subscription: SubscriptionDetail) => {
    setSelectedSubscription(subscription); // full object
    fetchPayments(subscription.subscription_id);
    setIsPaymentViewModalVisible(true);
  };
  const closePaymentNowModal = () => {
    form.resetFields();
    setIsPaymentNowModalVisible(false);
    setSelectedSubscription(null);
  };
  const closePaymentViewModal = () => {
    setIsPaymentViewModalVisible(false);
  };
  const handlePaymentSubmit = async (values: any) => {
    try {
      const payload = {
        sp_subscription_id: selectedSubscription,
        sp_payment_amount: values.sp_payment_amount,
        sp_payment_date: values.sp_payment_date.format('YYYY-MM-DD'),
        sp_payment_method: values.sp_payment_method,
        // Razorpay-specific fields
        sp_razorpay_payment_id: values.sp_razorpay_payment_id,
        sp_razorpay_order_id: values.sp_razorpay_order_id,
        sp_razorpay_signature: values.sp_razorpay_signature,
        // Cash-specific fields
        sp_cash_received_by: values.sp_cash_received_by,
        sp_cash_receipt_number: values.sp_cash_receipt_number,
      };

      const response = await apiRequest({
        method: 'post',
        url: '/add-subscription-payment',  // Your payment endpoint here
        data: payload,
      });

      if (response?.subscription_id) {
        showSuccess('Payment submitted successfully');
        form.resetFields();            // 3. Reset form
        closePaymentNowModal();          // 5. (optional) refresh data
      }
    } catch (error) {
      showError('Failed to submit payment');
      console.error(error);
    }
  };

  const fetchPayments = async (subscriptionId: number | null) => {
    if (!subscriptionId) return;

    try {
      const data = await apiRequest<Payment[]>({
        method: 'get',
        url: `/subscription-payments?subscription_id=${subscriptionId}`,
      });
      setPayments(data);
    } catch (error) {
      showError('Failed to fetch payments');
      console.error(error);
    }
  };
  const paymentColumns: TableColumnsType<Payment> = [
    { title: 'Payment ID', dataIndex: 'sp_payment_id', key: 'id' },
    { title: 'Amount', dataIndex: 'sp_payment_amount', key: 'amount', render: (val: number) => `₹ ${val}` },
    { title: 'Date', dataIndex: 'sp_payment_date', key: 'date' },
    { title: 'Method', dataIndex: 'sp_payment_method', key: 'method' },
    { title: 'Status', dataIndex: 'sp_payment_status', key: 'status' },
  ];

  const handleCreate = () => {
    fetchSubscription();
  };

  const showCreateModal = () => {
    setIsEditMode(false);
    setInitialValues(null);
    setIsModalVisible(true);
  };

  const hideCreateModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
  };



  const handleStatusToggle = async (subscriptionId: number) => {
    setSubscriptions((prev) =>
      prev.map((item) =>
        item.subscription_id === subscriptionId
          ? { ...item, subscription_status: item.subscription_status === 1 ? 0 : 1 }
          : item
      )
    );

    try {
      await apiRequest({
        method: 'put',
        url: `/update-status-subscription/${subscriptionId}`,
      });
      showSuccess('subscription status updated');
    } catch (error) {
      showError('Failed to update status');
      setSubscriptions((prev) =>
        prev.map((item) =>
          item.subscription_id === subscriptionId
            ? { ...item, subscription_status: item.subscription_status === 1 ? 0 : 1 }
            : item
        )
      );
    }
  };

  const handleTableChange: TableProps<Subscription>['onChange'] = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };


  useEffect(() => {
    console.log("payments", payments)
  }, [payments]);

  const columns: TableColumnsType<Subscription> = [
    {
      title: 'Organization Name',
      dataIndex: 'organization_name',
      key: 'organization_name',
      filters: Array.from(new Set(subscriptions.map((s) => s.organization_name))).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.organization_name || null,
      onFilter: (value, record) => record.organization_name.includes(value as string),
      sorter: (a, b) => a.organization_name.localeCompare(b.organization_name),
      sortOrder: sortedInfo.columnKey === 'organization_name' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => <div>{record.organization_name}</div>,
    },
    {
      title: 'Package Name',
      dataIndex: 'package_name',
      key: 'package_name',

      filters: Array.from(
        new Set(subscriptions.map(m => m.package_name || 'Root'))
      ).map(name => ({
        text: name,
        value: name,
      })),

      filteredValue: filteredInfo.package_name || null,

      onFilter: (value, record) =>
        (record.package_name || 'Root') === value,

      sorter: (a, b) => {
        const nameA = String(a.package_name || 'Root');
        const nameB = String(b.package_name || 'Root');
        return nameA.localeCompare(nameB);
      },

      sortOrder: sortedInfo.columnKey === 'package_name' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,

      render: (_, record) => (
        <div>{record.package_name || 'Root'}</div>
      ),
    },
    {
      title: 'Expried ON ',
      dataIndex: 'subscription_expiry_date',
      key: 'subscription_expiry_date',
      render: (_, record) => <div>{record.subscription_expiry_date}</div>,
    },
  ];


  // Conditionally add action column
  if (currentModule?.right_edit === 'Y' || currentModule?.right_delete === 'Y') {
    columns.push({
      title: 'Action',
      width: 200,
      render: (_: any, record: Subscription) => (
        <Space size="middle">
          {currentModule?.right_edit === 'Y' && (
            <>
              <Button style={{ marginRight: 8 }} onClick={() => openPaymentViewModal(record)}>
                View Payments
              </Button>
              {record.subscription_expire_status !== 'expired' && (
                <>
                  <Switch
                    checkedChildren="Active"
                    unCheckedChildren="Inactive"
                    checked={!!record.subscription_status}
                    onChange={() => handleStatusToggle(record.subscription_id)}
                  />
                  <Button onClick={() => handleEditClick(record)} className="px-8">
                    <EditOutlined />
                  </Button>

                  <Button type="primary" onClick={() => openPaymentNowModal(record)}>
                    Make Payment
                  </Button>
                </>
              )}

            </>
          )}
        </Space>
      ),
    });
  }


  return (
    <>
      <Space
        style={{
          display: 'flex',
          marginBottom: 16,
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Breadcrumb style={{ display: 'inline-block', margin: 0 }}>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>View Subscription</Breadcrumb.Item>
        </Breadcrumb>

        {currentModule?.right_add === 'Y' && (
          <Flex gap="small">
            <Button type="primary" onClick={showCreateModal}>
              Add Subscription
            </Button>
          </Flex>
        )}
      </Space>


      <div
        style={{
          minHeight: 360,
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        <Table<Subscription>
          tableLayout="auto"
          className={`${styles.customTable} scrollable-table`}
          columns={columns}
          onChange={handleTableChange}
          rowKey="subscription_id"
          dataSource={subscriptions.slice(
            (pagination.current - 1) * pagination.pageSize,
            pagination.current * pagination.pageSize
          )}
          bordered
          pagination={false}
          scroll={{ y: 37 * 15 }}
          locale={{ emptyText: 'No subscription found' }}
          footer={() => (
            <Flex justify="space-between">
              <span>
                Showing {(pagination.current - 1) * pagination.pageSize + 1}–
                {Math.min(pagination.current * pagination.pageSize, subscriptions.length)} of{' '}
                {subscriptions.length}
              </span>
              <Pagination
                {...pagination}
                total={subscriptions.length}
                showSizeChanger
                onChange={(page, pageSize) => setPagination({ current: page, pageSize })}
              />
            </Flex>
          )}
        />
      </div>

      <SubscriptionModal
        visible={isModalVisible}
        onCancel={hideCreateModal}
        onCreate={handleCreate}
        isEditMode={isEditMode}
        subscriptions={subscriptions}
        initialValues={initialValues}
      />
      <PaymentModal
        visible={ispaymentnowModalVisible}
        onCancel={closePaymentNowModal}
        onSubmit={handlePaymentSubmit}
      />

      <PaymentDetailsModal
        ispaymentviewModalVisible={ispaymentviewModalVisible}
        closePaymentViewModal={() => setIsPaymentViewModalVisible(false)}
        subscription={selectedSubscription}
        payments={payments}
      />
    </>
  );
};

export default SubscriptionMaster;